import { useCallback, useEffect, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { SerpOverlayErrorState } from "./SerpOverlayErrorState";

import { SerpOverlayIframe, SerpOverlayTabPanel, SerpOverlayTabPanelLoader } from "./styles";
import type { SerpSnapshotDataRecords } from "./types";

type Props = {
    records: SerpSnapshotDataRecords;
    activePageIndex: number;
    iframeErrorsByPageIndex: Record<number, boolean>;
    onIframeError: (pageIndex: number) => void;
};

const createPageIndexSet = (indices: number[]) => new Set(indices);

export const SerpOverlayIframePanels = ({
    records,
    activePageIndex,
    iframeErrorsByPageIndex,
    onIframeError,
}: Props) => {
    const { translate } = useTranslate();
    const [visitedPageIndices, setVisitedPageIndices] = useState(() => createPageIndexSet([activePageIndex]));
    const [loadedPageIndices, setLoadedPageIndices] = useState<Set<number>>(() => new Set());

    const iframeTitle = translate("rankTracker.serpOverlay.iframe.title");

    useEffect(() => {
        setVisitedPageIndices(createPageIndexSet([0]));
        setLoadedPageIndices(new Set());
    }, [records]);

    useEffect(() => {
        setVisitedPageIndices((currentIndices) => {
            if (currentIndices.has(activePageIndex)) {
                return currentIndices;
            }
            return createPageIndexSet([...currentIndices, activePageIndex]);
        });
    }, [activePageIndex]);

    const handleIframeLoad = useCallback((pageIndex: number) => {
        setLoadedPageIndices((currentIndices) => {
            if (currentIndices.has(pageIndex)) {
                return currentIndices;
            }
            return createPageIndexSet([...currentIndices, pageIndex]);
        });
    }, []);

    return (
        <>
            {records?.map((record, pageIndex) => {
                if (!visitedPageIndices.has(pageIndex)) {
                    return null;
                }

                const isActive = pageIndex === activePageIndex;
                const hasIframeError = iframeErrorsByPageIndex[pageIndex] ?? false;
                const isPageLoading = isActive && !loadedPageIndices.has(pageIndex) && !hasIframeError;

                return (
                    <SerpOverlayTabPanel
                        key={record.link}
                        $isActive={isActive}
                        data-automation={`serp-overlay-iframe-${pageIndex}`}
                    >
                        {hasIframeError ? (
                            <SerpOverlayErrorState />
                        ) : (
                            <>
                                {isPageLoading ? (
                                    <SerpOverlayTabPanelLoader data-automation="serp-overlay-page-loading">
                                        <PixelPlaceholderLoader
                                            width="100%"
                                            height="100%"
                                        />
                                    </SerpOverlayTabPanelLoader>
                                ) : null}

                                <SerpOverlayIframe
                                    src={record.link || ""}
                                    title={iframeTitle}
                                    onLoad={() => handleIframeLoad(pageIndex)}
                                    onError={() => onIframeError(pageIndex)}
                                />
                            </>
                        )}
                    </SerpOverlayTabPanel>
                );
            })}
        </>
    );
};
